import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, CircularProgress, MenuItem, SelectChangeEvent, Stack, Typography } from '@mui/material';
import { useFormik } from 'formik';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { IPaginatedResponse } from '../../api/httpClient';
import { IGetVesselPreviewResponse } from '../../api/scenario/iApiResponse';
import * as ScenarioHelper from '../../api/scenario/scenarioHelper';
import {
    getCoefficentC3,
    getEffectivePowerUsedByThePropeller,
    getHorizontalDistanceBetweenPropellerAxisAndTheSlopeToe,
    getPropellerDistanceFromQuayL,
    getVerticalDistanceBetweenTheAxisOfThePropellerAndTheKell,
} from '../../calculation';
import {
    distanceBetweenPropellerAxes,
    enginePhysicalData,
    maximumPowerOfThePropeller,
    numberOfPropellers,
    percentageOfUseInPortManeuvers,
    propellerDiameter,
    propellerDirectionWithRespectToTheQuay,
    typeOfPropeller,
} from '../../conversion/engine';
import {
    coefficentC3,
    draft,
    effectivePowerUsedByThePropeller,
    horizontalDistanceBetweenPropellerAxisAndTheSlopeToe,
    propellerDistanceFromQuayL,
    propellerEquippedWithNozzles,
    scenarioPhysicalData,
    verticalDistanceBetweenTheAxisOfThePropellerAndTheKell,
    vesselBeamBs,
    vesselType,
} from '../../conversion/scenario';
import DropDown from '../../shared/component/DropDown';
import InputText from '../../shared/component/InputText';
import { ENumberOfPropellers, EPropellerDirectionWithRespectToTheQuay, EPropellerEquippedWithNozzles, ETypeOfPropeller, ETypeOfQuay, EUnitOfMeasure } from '../../shared/enum';
import { IEngine, IInputEngine, IInputScenario, IScenario, TInputEngine, TInputScenario } from '../../shared/iProject';
import { cleanAlert } from '../../store/alert/alertSlice';
import { useAppDispatch, useAppSelector } from '../../store/hook';
import { selectProject, setEngine, setScenario } from '../../store/project/projectSlice';
import { cleanResult } from '../../store/result/resultSlice';
import { selectUser } from '../../store/user/userSlice';
import { CurrentScenarioContext } from '../MultiScenario';
import { cleanEngine } from '../../store/engine/engineSlice';
import { cleanMap } from '../../store/map/mapSlice';
import { cloneDeep } from 'lodash';
import Swal from 'sweetalert2';
import { designWaterLevel } from '../../conversion/geometry';
import { degreesToRadians } from '../../shared/util';

interface IInputProps {}

interface IInput {
    vesselCharacteristics: IInputScenario;
    engine: IInputEngine;
}

const customTEU = 'custom';
let oldTypeOfQuay: ETypeOfQuay;

const Input = (props: IInputProps) => {
    const currentIndex = React.useContext(CurrentScenarioContext);

    let timer = React.useRef<NodeJS.Timeout>();

    const [isLoading, setIsLoading] = React.useState(false);
    const [scenarioPreview, setScenarioPreview] = React.useState<IPaginatedResponse<IGetVesselPreviewResponse>>();
    const [engines, setEngines] = React.useState<IEngine[]>();

    const project = useAppSelector(selectProject);
    const user = useAppSelector(selectUser);
    const dispatch = useAppDispatch();

    const { t } = useTranslation();

    const fetchScenarioPreview = async () => {
        setIsLoading(true);
        let res = await ScenarioHelper.getScenarioPreviewAsync(undefined, undefined, undefined, user.data!.isAdmin);
        setScenarioPreview(res.data);
        setIsLoading(false);
    };

    const fetchEngineByScenario = async () => {
        setIsLoading(true);
        let res = await ScenarioHelper.getScenarioByIdAsync(project.data!.scenarios![currentIndex].baseScenarioId!);
        setEngines(res.data.engines);
        setIsLoading(false);
    };

    React.useEffect(() => {
        oldTypeOfQuay = project.data!.geometry!.typeOfQuay;
        fetchEngineByScenario();
        fetchScenarioPreview();
    }, []);

    const getDistanceBetweenPropellerAxesByUnitOfMeasure = () => (project.data?.project?.unitOfMeasure == EUnitOfMeasure.Metric ? 3.4 : 11.15);

    let input: IInput = {
        vesselCharacteristics: {
            vesselType: project.data!.scenarios![currentIndex].vesselType,
            propellerEquippedWithNozzles: project.data!.scenarios![currentIndex].propellerEquippedWithNozzles,
            effectivePowerUsedByThePropeller: project.data!.scenarios![currentIndex].effectivePowerUsedByThePropeller,
            coefficentC3: project.data!.scenarios![currentIndex].coefficentC3,
            verticalDistanceBetweenTheAxisOfThePropellerAndTheKell: project.data!.scenarios![currentIndex].verticalDistanceBetweenTheAxisOfThePropellerAndTheKell,
            draft: project.data!.scenarios![currentIndex].draft,
            vesselBeamBs: project.data!.scenarios![currentIndex].vesselBeamBs,
            propellerDistanceFromQuayL: project.data!.scenarios![currentIndex].propellerDistanceFromQuayL,
            horizontalDistanceBetweenPropellerAxisAndTheSlopeToe: project.data!.scenarios![currentIndex].horizontalDistanceBetweenPropellerAxisAndTheSlopeToe,
        },
        engine: {
            typeOfPropeller: project.data!.engines![currentIndex].typeOfPropeller,
            numberOfPropellers: project.data!.engines![currentIndex].numberOfPropellers,
            distanceBetweenPropellerAxes: project.data!.engines![currentIndex].distanceBetweenPropellerAxes ?? getDistanceBetweenPropellerAxesByUnitOfMeasure(),
            propellerDiameter: project.data!.engines![currentIndex].propellerDiameter,
            maximumPowerOfThePropeller: project.data!.engines![currentIndex].maximumPowerOfThePropeller,
            percentageOfUseInPortManeuvers: project.data!.engines![currentIndex].percentageOfUseInPortManeuvers,
            propellerDirectionWithRespectToTheQuay: project.data!.engines![currentIndex].propellerDirectionWithRespectToTheQuay,
        },
    };

    const inputMerge = {
        vesselType: input.vesselCharacteristics.vesselType,
        typeOfPropeller: input.engine.typeOfPropeller,
        propellerDirectionWithRespectToTheQuay: input.engine.propellerDirectionWithRespectToTheQuay,
        numberOfPropellers: input.engine.numberOfPropellers,
        draft: input.vesselCharacteristics.draft,
        vesselBeamBs: input.vesselCharacteristics.vesselBeamBs,
        distanceBetweenPropellerAxes: input.engine.distanceBetweenPropellerAxes,
        propellerDiameter: input.engine.propellerDiameter,
        propellerEquippedWithNozzles: input.vesselCharacteristics.propellerEquippedWithNozzles,
        maximumPowerOfThePropeller: input.engine.maximumPowerOfThePropeller,
        percentageOfUseInPortManeuvers: input.engine.percentageOfUseInPortManeuvers,
        effectivePowerUsedByThePropeller: input.vesselCharacteristics.effectivePowerUsedByThePropeller,
        coefficentC3: input.vesselCharacteristics.coefficentC3,
        verticalDistanceBetweenTheAxisOfThePropellerAndTheKell: input.vesselCharacteristics.verticalDistanceBetweenTheAxisOfThePropellerAndTheKell,
        propellerDistanceFromQuayL: input.vesselCharacteristics.propellerDistanceFromQuayL,
        horizontalDistanceBetweenPropellerAxisAndTheSlopeToe: input.vesselCharacteristics.horizontalDistanceBetweenPropellerAxisAndTheSlopeToe,
    };

    const formik = useFormik({
        initialValues: {
            ...inputMerge,
        },
        validationSchema: yup.object({
            vesselType: yup.string().required(),
            typeOfPropeller: yup.number().required(),
            propellerDirectionWithRespectToTheQuay: yup.number().required(),
            numberOfPropellers: yup.number().required(),
            distanceBetweenPropellerAxes: yup.number().required(),
            propellerDiameter: yup.number().required(),
            propellerEquippedWithNozzles: yup.boolean().required(),
            maximumPowerOfThePropeller: yup.number().required(),
            percentageOfUseInPortManeuvers: yup.number().required(),
            effectivePowerUsedByThePropeller: yup.number().required(),
            coefficentC3: yup.number().required(),
            verticalDistanceBetweenTheAxisOfThePropellerAndTheKell: yup.number().required(),
            draft: yup.number().required(),
            vesselBeamBs: yup.number().required(),
            propellerDistanceFromQuayL: yup.number().required(),
            horizontalDistanceBetweenPropellerAxisAndTheSlopeToe: yup.number().required(),
        }),
        onSubmit: values => {},
    });

    React.useEffect(() => {
        if (project.data!.geometry!.typeOfQuay !== oldTypeOfQuay) {
            let propellerDistanceFromQuayL = getPropellerDistanceFromQuayL(
                formik.values.propellerDirectionWithRespectToTheQuay,
                project.data!.geometry!.typeOfQuay,
                formik.values.typeOfPropeller,
                formik.values.vesselBeamBs,
                project.data?.project?.unitOfMeasure!,
            );
            let horizontalDistanceBetweenPropellerAxisAndTheSlopeToe = getHorizontalDistanceBetweenPropellerAxisAndTheSlopeToe(
                formik.values.propellerDirectionWithRespectToTheQuay,
                project.data!.geometry!.typeOfQuay,
                formik.values.typeOfPropeller,
                formik.values.vesselBeamBs,
                project.data?.project?.unitOfMeasure!,
            );
            formik.setValues({
                ...formik.values,
                propellerDistanceFromQuayL,
                horizontalDistanceBetweenPropellerAxisAndTheSlopeToe,
            });
            dispatch(setScenario({ index: currentIndex, scenario: { ...project.data!.scenarios![currentIndex], propellerDistanceFromQuayL, horizontalDistanceBetweenPropellerAxisAndTheSlopeToe } }));
            dispatch(cleanResult());
            dispatch(cleanEngine());
            dispatch(cleanMap());
            dispatch(cleanAlert());
            oldTypeOfQuay = project.data!.geometry!.typeOfQuay;
        }
    }, [project.data!.geometry!.typeOfQuay]);

    const checkAlert = (name: string, value: number) => {
        if (timer.current) {
            clearTimeout(timer.current);
            timer.current = undefined;
        }
        if (name == propellerDiameter) {
            let maxValueLabel = project.data?.project?.unitOfMeasure == EUnitOfMeasure.Metric ? `20 ${t('meters')}` : `65.6 ${t('feet')}`;
            if ((value as number) < 0 || (value as number) > parseFloat(maxValueLabel.split(' ')[0])) {
                timer.current = setTimeout(() => {
                    Swal.fire({
                        text: t('propellerDiameterLimit', { maxValueLabel }),
                        icon: 'warning',
                    });
                }, 2500);
            }
        }
        if (name == maximumPowerOfThePropeller) {
            if ((value as number) < 0 || (value as number) > 999999) {
                timer.current = setTimeout(() => {
                    Swal.fire({
                        text: t('maximumPowerOfThePropellerLimit'),
                        icon: 'warning',
                    });
                }, 2500);
            }
        }
        if (name == percentageOfUseInPortManeuvers) {
            if ((value as number) < 0 || (value as number) > 100) {
                timer.current = setTimeout(() => {
                    Swal.fire({
                        text: t('percentageOfUseInPortManeuversLimit'),
                        icon: 'warning',
                    });
                }, 2500);
            }
        }
        if (name == verticalDistanceBetweenTheAxisOfThePropellerAndTheKell) {
            if ((value as number) < formik.values.propellerDiameter / 2 || (value as number) > formik.values.draft) {
                timer.current = setTimeout(() => {
                    Swal.fire({
                        text: t('verticalDistanceBetweenTheAxisOfThePropellerAndTheKellLimit'),
                        icon: 'warning',
                    });
                }, 2500);
            }
        }
        if (name == draft) {
            if ((value as number) < 0 || (value as number) > project.data?.geometry?.minimumWaterDepth!) {
                timer.current = setTimeout(() => {
                    Swal.fire({
                        text: t('draftLimit'),
                        icon: 'warning',
                    });
                }, 2500);
            }
        }
        if (name == vesselBeamBs) {
            let maxValueLabel = project.data?.project?.unitOfMeasure == EUnitOfMeasure.Metric ? `199 ${t('meters')}` : `652.8 ${t('feet')}`;
            if ((value as number) < 0 || (value as number) > parseFloat(maxValueLabel.split(' ')[0])) {
                timer.current = setTimeout(() => {
                    Swal.fire({
                        text: t('vesselBeamBsLimit', { maxValueLabel }),
                        icon: 'warning',
                    });
                }, 2500);
            }
        }
        if (name == propellerDistanceFromQuayL && project.data?.geometry?.typeOfQuay == ETypeOfQuay.Vertical) {
            if (formik.values.typeOfPropeller == ETypeOfPropeller.MainPropeller && formik.values.propellerDirectionWithRespectToTheQuay == EPropellerDirectionWithRespectToTheQuay.Transversal) {
                if ((value as number) < project.data?.geometry?.distanceBetweenVesselAndQuay!) {
                    timer.current = setTimeout(() => {
                        Swal.fire({
                            text: t('propellerDistanceFromQuayLLimit'),
                            icon: 'warning',
                        });
                    }, 2500);
                }
            } else if (formik.values.typeOfPropeller == ETypeOfPropeller.BowThruster) {
                if (
                    (value as number) < project.data?.geometry?.distanceBetweenVesselAndQuay! ||
                    (value as number) > project.data?.geometry?.distanceBetweenVesselAndQuay! + formik.values.vesselBeamBs
                ) {
                    timer.current = setTimeout(() => {
                        Swal.fire({
                            text: t('propellerDistanceFromQuayLLimit'),
                            icon: 'warning',
                        });
                    }, 2500);
                }
            }
        }
        if (name == horizontalDistanceBetweenPropellerAxisAndTheSlopeToe && project.data?.geometry?.typeOfQuay !== ETypeOfQuay.Vertical) {
            if (formik.values.typeOfPropeller == ETypeOfPropeller.MainPropeller && formik.values.propellerDirectionWithRespectToTheQuay == EPropellerDirectionWithRespectToTheQuay.Transversal) {
                if (
                    (value as number) <
                    project.data?.geometry?.distanceBetweenVesselAndQuay! - (0.3 * project.data?.geometry?.minimumWaterDepth!) / Math.tan(degreesToRadians(project.data?.geometry?.quaySlope!))
                ) {
                    timer.current = setTimeout(() => {
                        Swal.fire({
                            text: t('horizontalDistanceBetweenPropellerAxisAndTheSlopeToeLimit'),
                            icon: 'warning',
                        });
                    }, 2500);
                }
            } else if (formik.values.typeOfPropeller == ETypeOfPropeller.BowThruster) {
                if (
                    (value as number) <
                        project.data?.geometry?.distanceBetweenVesselAndQuay! - (0.3 * project.data?.geometry?.minimumWaterDepth!) / Math.tan(degreesToRadians(project.data?.geometry?.quaySlope!)) ||
                    (value as number) >
                        project.data?.geometry?.distanceBetweenVesselAndQuay! -
                            (0.3 * project.data?.geometry?.minimumWaterDepth!) / Math.tan(degreesToRadians(project.data?.geometry?.quaySlope!)) +
                            formik.values.vesselBeamBs
                ) {
                    timer.current = setTimeout(() => {
                        Swal.fire({
                            text: t('horizontalDistanceBetweenPropellerAxisAndTheSlopeToeLimit'),
                            icon: 'warning',
                        });
                    }, 2500);
                }
            }
        }
    };

    const handleChangeVessel = async (event: React.ChangeEvent<any> | SelectChangeEvent<any>) => {
        let { name, value } = event.target;
        value = name !== vesselType ? parseFloat(value) : value;
        if (!isNaN(value) || name == vesselType) {
            let scenario: IScenario = {
                vesselType: customTEU,
                propellerEquippedWithNozzles: formik.values.propellerEquippedWithNozzles,
                effectivePowerUsedByThePropeller: formik.values.effectivePowerUsedByThePropeller,
                coefficentC3: formik.values.coefficentC3,
                verticalDistanceBetweenTheAxisOfThePropellerAndTheKell: formik.values.verticalDistanceBetweenTheAxisOfThePropellerAndTheKell,
                draft: formik.values.draft,
                vesselBeamBs: formik.values.vesselBeamBs,
                propellerDistanceFromQuayL: formik.values.propellerDistanceFromQuayL,
                horizontalDistanceBetweenPropellerAxisAndTheSlopeToe: formik.values.horizontalDistanceBetweenPropellerAxisAndTheSlopeToe,
                id: project.data!.scenarios![currentIndex].id,
                geometryId: project.data!.scenarios![currentIndex].geometryId,
                baseScenarioId: project.data!.scenarios![currentIndex].baseScenarioId,
                isBaseScenario: project.data!.scenarios![currentIndex].isBaseScenario,
                [name]: value,
            };
            if (name == vesselType) {
                setIsLoading(true);
                let baseScenarioId = scenarioPreview!.value.find(x => x.vesselType == value)!.id;
                let res = await ScenarioHelper.getScenarioByIdAsync(baseScenarioId);
                let engine = res.data.engines?.find(
                    x =>
                        x.typeOfPropeller == formik.values.typeOfPropeller &&
                        x.propellerDirectionWithRespectToTheQuay == formik.values.propellerDirectionWithRespectToTheQuay &&
                        x.numberOfPropellers == formik.values.numberOfPropellers,
                )!;
                setEngines(res.data.engines!);
                scenario = {
                    vesselType: res.data.vesselType,
                    propellerEquippedWithNozzles: res.data.propellerEquippedWithNozzles,
                    effectivePowerUsedByThePropeller: getEffectivePowerUsedByThePropeller(engine.maximumPowerOfThePropeller, engine.percentageOfUseInPortManeuvers),
                    coefficentC3: getCoefficentC3(res.data.propellerEquippedWithNozzles),
                    verticalDistanceBetweenTheAxisOfThePropellerAndTheKell: getVerticalDistanceBetweenTheAxisOfThePropellerAndTheKell(
                        engine.propellerDiameter,
                        engine.typeOfPropeller,
                        project.data!.project!.unitOfMeasure!,
                    )!,
                    draft: res.data.draft,
                    vesselBeamBs: res.data.vesselBeamBs,
                    propellerDistanceFromQuayL: getPropellerDistanceFromQuayL(
                        engine.propellerDirectionWithRespectToTheQuay,
                        project.data!.geometry!.typeOfQuay,
                        engine.typeOfPropeller,
                        res.data.vesselBeamBs,
                        project.data?.project?.unitOfMeasure!,
                    ),
                    horizontalDistanceBetweenPropellerAxisAndTheSlopeToe: getHorizontalDistanceBetweenPropellerAxisAndTheSlopeToe(
                        engine.propellerDirectionWithRespectToTheQuay,
                        project.data!.geometry!.typeOfQuay,
                        engine.typeOfPropeller,
                        res.data.vesselBeamBs,
                        project.data?.project?.unitOfMeasure!,
                    ),
                    id: project.data!.scenarios![currentIndex].id,
                    geometryId: project.data!.scenarios![currentIndex].geometryId,
                    isBaseScenario: project.data!.scenarios![currentIndex].isBaseScenario,
                    baseScenarioId: baseScenarioId,
                };
                formik.setValues({ ...formik.values, ...engine, ...scenario });
                dispatch(setEngine({ index: currentIndex, engine }));
                setIsLoading(false);
            } else if (name == propellerEquippedWithNozzles) {
                scenario.coefficentC3 = getCoefficentC3(value);
                formik.setValues({ ...formik.values, ...scenario });
            } else if (name == vesselBeamBs) {
                scenario.propellerDistanceFromQuayL = getPropellerDistanceFromQuayL(
                    formik.values.propellerDirectionWithRespectToTheQuay,
                    project.data!.geometry!.typeOfQuay,
                    formik.values.typeOfPropeller,
                    value,
                    project.data?.project?.unitOfMeasure!,
                );
                scenario.horizontalDistanceBetweenPropellerAxisAndTheSlopeToe = getHorizontalDistanceBetweenPropellerAxisAndTheSlopeToe(
                    formik.values.propellerDirectionWithRespectToTheQuay,
                    project.data!.geometry!.typeOfQuay,
                    formik.values.typeOfPropeller,
                    value,
                    project.data?.project?.unitOfMeasure!,
                );
                formik.setValues({
                    ...formik.values,
                    ...scenario,
                });
            } else {
                formik.setValues({ ...formik.values, ...scenario });
            }
            dispatch(setScenario({ index: currentIndex, scenario }));
            dispatch(cleanResult());
            dispatch(cleanEngine());
            dispatch(cleanMap());
            dispatch(cleanAlert());

            checkAlert(name, value);
        }
    };

    const handleChangeEngine = (event: React.ChangeEvent<any> | SelectChangeEvent<any>) => {
        let { name, value } = event.target;
        value = parseFloat(value);
        if (!isNaN(value)) {
            let engine: IEngine = {
                id: project.data!.engines![currentIndex].id,
                typeOfPropeller: formik.values.typeOfPropeller,
                propellerDiameter: formik.values.propellerDiameter,
                distanceBetweenPropellerAxes: formik.values.distanceBetweenPropellerAxes,
                maximumPowerOfThePropeller: formik.values.maximumPowerOfThePropeller,
                percentageOfUseInPortManeuvers: formik.values.percentageOfUseInPortManeuvers,
                numberOfPropellers: formik.values.numberOfPropellers,
                propellerDirectionWithRespectToTheQuay: formik.values.propellerDirectionWithRespectToTheQuay,
                isBaseEngine: project.data!.engines![currentIndex].isBaseEngine,
                baseEngineId: project.data!.engines![currentIndex].baseEngineId,
                status: project.data!.engines![currentIndex].status,
                [name]: value,
            };
            let {
                propellerDistanceFromQuayL,
                horizontalDistanceBetweenPropellerAxisAndTheSlopeToe,
                effectivePowerUsedByThePropeller,
                verticalDistanceBetweenTheAxisOfThePropellerAndTheKell,
                vesselType,
            } = formik.values;

            if (name == typeOfPropeller) {
                if (value == ETypeOfPropeller.BowThruster) {
                    engine.propellerDirectionWithRespectToTheQuay = EPropellerDirectionWithRespectToTheQuay.Transversal;
                }
                let newEngine = engines!.find(
                    x => x.typeOfPropeller == value && x.propellerDirectionWithRespectToTheQuay == engine.propellerDirectionWithRespectToTheQuay && x.numberOfPropellers == engine.numberOfPropellers,
                );
                if (newEngine) engine = newEngine;

                propellerDistanceFromQuayL = getPropellerDistanceFromQuayL(
                    engine.propellerDirectionWithRespectToTheQuay,
                    project.data!.geometry!.typeOfQuay,
                    value,
                    formik.values.vesselBeamBs,
                    project.data?.project?.unitOfMeasure!,
                );
                horizontalDistanceBetweenPropellerAxisAndTheSlopeToe = getHorizontalDistanceBetweenPropellerAxisAndTheSlopeToe(
                    engine.propellerDirectionWithRespectToTheQuay,
                    project.data!.geometry!.typeOfQuay,
                    engine.typeOfPropeller,
                    formik.values.vesselBeamBs,
                    project.data?.project?.unitOfMeasure!,
                );
                verticalDistanceBetweenTheAxisOfThePropellerAndTheKell = getVerticalDistanceBetweenTheAxisOfThePropellerAndTheKell(
                    engine.propellerDiameter,
                    value,
                    project.data!.project!.unitOfMeasure!,
                )!;
                vesselType = newEngine ? formik.values.vesselType : customTEU;
                formik.setValues({
                    ...formik.values,
                    ...engine,
                    vesselType,
                    propellerDistanceFromQuayL,
                    horizontalDistanceBetweenPropellerAxisAndTheSlopeToe,
                    verticalDistanceBetweenTheAxisOfThePropellerAndTheKell,
                });
            } else if (name == propellerDirectionWithRespectToTheQuay) {
                let newEngine = engines!.find(
                    x => x.typeOfPropeller == formik.values.typeOfPropeller && x.propellerDirectionWithRespectToTheQuay == value && x.numberOfPropellers == engine.numberOfPropellers,
                );
                if (newEngine) engine = newEngine;
                propellerDistanceFromQuayL = getPropellerDistanceFromQuayL(
                    value,
                    project.data!.geometry!.typeOfQuay,
                    formik.values.typeOfPropeller,
                    formik.values.vesselBeamBs,
                    project.data?.project?.unitOfMeasure!,
                );
                horizontalDistanceBetweenPropellerAxisAndTheSlopeToe = getHorizontalDistanceBetweenPropellerAxisAndTheSlopeToe(
                    engine.propellerDirectionWithRespectToTheQuay,
                    project.data!.geometry!.typeOfQuay,
                    engine.typeOfPropeller,
                    formik.values.vesselBeamBs,
                    project.data?.project?.unitOfMeasure!,
                );
                vesselType = newEngine ? formik.values.vesselType : customTEU;
                formik.setValues({
                    ...formik.values,
                    ...engine,
                    vesselType,
                    propellerDistanceFromQuayL,
                    horizontalDistanceBetweenPropellerAxisAndTheSlopeToe,
                });
            } else if (name == numberOfPropellers) {
                let newEngine = cloneDeep(
                    engines!.find(
                        x => x.typeOfPropeller == engine.typeOfPropeller && x.propellerDirectionWithRespectToTheQuay == engine.propellerDirectionWithRespectToTheQuay && x.numberOfPropellers == value,
                    ),
                );
                if (newEngine) engine = newEngine;
                propellerDistanceFromQuayL = getPropellerDistanceFromQuayL(
                    engine.propellerDirectionWithRespectToTheQuay,
                    project.data!.geometry!.typeOfQuay,
                    value,
                    formik.values.vesselBeamBs,
                    project.data?.project?.unitOfMeasure!,
                );
                horizontalDistanceBetweenPropellerAxisAndTheSlopeToe = getHorizontalDistanceBetweenPropellerAxisAndTheSlopeToe(
                    engine.propellerDirectionWithRespectToTheQuay,
                    project.data!.geometry!.typeOfQuay,
                    engine.typeOfPropeller,
                    formik.values.vesselBeamBs,
                    project.data?.project?.unitOfMeasure!,
                );
                vesselType = newEngine ? formik.values.vesselType : customTEU;
                if (value == ENumberOfPropellers.Single) engine.distanceBetweenPropellerAxes = undefined;
                else engine.distanceBetweenPropellerAxes = newEngine ? formik.values.distanceBetweenPropellerAxes : getDistanceBetweenPropellerAxesByUnitOfMeasure();
                formik.setValues({
                    ...formik.values,
                    ...engine,
                    vesselType,
                    propellerDistanceFromQuayL,
                    horizontalDistanceBetweenPropellerAxisAndTheSlopeToe,
                });
            } else if (name == maximumPowerOfThePropeller) {
                effectivePowerUsedByThePropeller = getEffectivePowerUsedByThePropeller(value, engine.percentageOfUseInPortManeuvers);
                vesselType = customTEU;
                formik.setValues({
                    ...formik.values,
                    ...engine,
                    effectivePowerUsedByThePropeller,
                    vesselType,
                });
            } else if (name == percentageOfUseInPortManeuvers) {
                if (value < 0) return (value = 0);
                if (value > 100) return (value = 100);
                effectivePowerUsedByThePropeller = getEffectivePowerUsedByThePropeller(engine.maximumPowerOfThePropeller, value);
                vesselType = customTEU;
                formik.setValues({
                    ...formik.values,
                    ...engine,
                    effectivePowerUsedByThePropeller,
                    vesselType,
                });
            } else if (name == propellerDiameter) {
                verticalDistanceBetweenTheAxisOfThePropellerAndTheKell = getVerticalDistanceBetweenTheAxisOfThePropellerAndTheKell(
                    value,
                    engine.typeOfPropeller,
                    project.data!.project!.unitOfMeasure!,
                )!;
                vesselType = customTEU;
                formik.setValues({
                    ...formik.values,
                    ...engine,
                    verticalDistanceBetweenTheAxisOfThePropellerAndTheKell,
                    vesselType: customTEU,
                });
            } else {
                vesselType = customTEU;
                formik.setValues({ ...formik.values, ...engine, vesselType });
            }
            dispatch(
                setScenario({
                    index: currentIndex,
                    scenario: {
                        ...project.data!.scenarios![currentIndex],
                        propellerDistanceFromQuayL,
                        horizontalDistanceBetweenPropellerAxisAndTheSlopeToe,
                        effectivePowerUsedByThePropeller,
                        verticalDistanceBetweenTheAxisOfThePropellerAndTheKell,
                        vesselType,
                    },
                }),
            );
            dispatch(setEngine({ index: currentIndex, engine }));
            dispatch(cleanResult());
            dispatch(cleanEngine());
            dispatch(cleanMap());
            dispatch(cleanAlert());

            checkAlert(name, value);
        }
    };

    const handleHidden = (name: string) => {
        if (name == distanceBetweenPropellerAxes && formik.values[numberOfPropellers] == ENumberOfPropellers.Single) return true;
        if (name == propellerDistanceFromQuayL)
            return !(project.data!.geometry!.typeOfQuay == ETypeOfQuay.Vertical && formik.values[propellerDirectionWithRespectToTheQuay] == EPropellerDirectionWithRespectToTheQuay.Transversal);
        if (name == horizontalDistanceBetweenPropellerAxisAndTheSlopeToe)
            return !(project.data!.geometry!.typeOfQuay !== ETypeOfQuay.Vertical && formik.values[propellerDirectionWithRespectToTheQuay] == EPropellerDirectionWithRespectToTheQuay.Transversal);
        return false;
    };

    const handleDisabled = (name: string) => {
        if (name == effectivePowerUsedByThePropeller || name == coefficentC3) return true;
        return false;
    };

    const handleTextTooltip = (name: string, value: any) => {
        if (name == verticalDistanceBetweenTheAxisOfThePropellerAndTheKell) return t('verticalDistanceBetweenTheAxisOfThePropellerAndTheKellInfo');
        if (name == propellerDirectionWithRespectToTheQuay) return t('propellerDirectionWithRespectToTheQuayInfo');
        if (name == propellerEquippedWithNozzles) return t('propellerEquippedWithNozzlesInfo');
        if (name == percentageOfUseInPortManeuvers) return t('percentageOfUseInPortManeuversInfo');
        if (name == horizontalDistanceBetweenPropellerAxisAndTheSlopeToe) return t('horizontalDistanceBetweenPropellerAxisAndTheSlopeToeInfo');
        if (name == propellerDistanceFromQuayL) return t('propellerDistanceFromQuayLInfo');
        if (name == coefficentC3) return t('coefficentC3Info');
    };

    return (
        <>
            {isLoading && <CircularProgress />}
            {!isLoading && scenarioPreview?.value?.length! > 0 && engines?.length! > 0 && (
                <Stack variant='scrollable'>
                    <form onSubmit={formik.handleSubmit}>
                        <Accordion disableGutters defaultExpanded>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls='panel1a-content' id='panel1a-header'>
                                <Typography>{t('vesselProperties')}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                {Object.keys(inputMerge).map(x => {
                                    if (x == vesselType) {
                                        return (
                                            <DropDown inputLabel={t(x)} labelId={`${x}-label`} id={x} name={x} value={formik.values[x as TInputScenario]} onChange={handleChangeVessel}>
                                                {scenarioPreview?.value.map(x => (
                                                    <MenuItem value={x.vesselType}>{x.vesselType}</MenuItem>
                                                ))}
                                                <MenuItem style={{ display: 'none' }} value={customTEU}>
                                                    {t(customTEU)}
                                                </MenuItem>
                                            </DropDown>
                                        );
                                    } else if (x == propellerEquippedWithNozzles) {
                                        return (
                                            <DropDown
                                                textTooltip={handleTextTooltip(x, formik.values[x as TInputEngine])}
                                                inputLabel={t(x)}
                                                labelId={`${x}-label`}
                                                id={x}
                                                name={x}
                                                value={formik.values[x as TInputScenario]}
                                                onChange={handleChangeVessel}>
                                                <MenuItem value={EPropellerEquippedWithNozzles.Yes}>{t('yes')}</MenuItem>
                                                <MenuItem value={EPropellerEquippedWithNozzles.No}>{t('no')}</MenuItem>
                                            </DropDown>
                                        );
                                    } else if (x == typeOfPropeller) {
                                        return (
                                            <DropDown inputLabel={t(x)} labelId={`${x}-label`} id={x} name={x} value={formik.values[x as TInputEngine]} onChange={handleChangeEngine}>
                                                <MenuItem value={ETypeOfPropeller.MainPropeller}>{t('mainPropeller')}</MenuItem>
                                                <MenuItem value={ETypeOfPropeller.BowThruster}>{t('bowThruster')}</MenuItem>
                                            </DropDown>
                                        );
                                    } else if (x == propellerDirectionWithRespectToTheQuay) {
                                        return (
                                            <DropDown
                                                textTooltip={handleTextTooltip(x, formik.values[x as TInputEngine])}
                                                inputLabel={t(x)}
                                                labelId={`${x}-label`}
                                                id={x}
                                                name={x}
                                                value={formik.values[x as TInputEngine]}
                                                onChange={handleChangeEngine}>
                                                {formik.values.typeOfPropeller == ETypeOfPropeller.MainPropeller && (
                                                    <MenuItem value={EPropellerDirectionWithRespectToTheQuay.Parallel}>{t('parallel')}</MenuItem>
                                                )}
                                                <MenuItem value={EPropellerDirectionWithRespectToTheQuay.Transversal}>{t('transversal')}</MenuItem>
                                            </DropDown>
                                        );
                                    } else if (x == numberOfPropellers) {
                                        return (
                                            <DropDown inputLabel={t(x)} labelId={`${x}-label`} id={x} name={x} value={formik.values[x as TInputEngine]} onChange={handleChangeEngine}>
                                                <MenuItem value={ENumberOfPropellers.Single}>{ENumberOfPropellers.Single}</MenuItem>
                                                <MenuItem value={ENumberOfPropellers.Double}>{ENumberOfPropellers.Double}</MenuItem>
                                            </DropDown>
                                        );
                                    } else {
                                        return (
                                            <InputText
                                                key={x}
                                                hidden={handleHidden(x)}
                                                disabled={handleDisabled(x)}
                                                error={formik.touched[x as TInputScenario | TInputEngine] && Boolean(formik.errors[x as TInputScenario | TInputEngine])}
                                                helperText={formik.touched[x as TInputScenario | TInputEngine] && (formik.errors[x as TInputScenario | TInputEngine] as React.ReactNode)}
                                                customValue={formik.values[x as TInputScenario | TInputEngine]}
                                                textTooltip={handleTextTooltip(x, formik.values[x as TInputScenario | TInputEngine])}
                                                id={x}
                                                isNumber={typeof inputMerge[x as TInputScenario | TInputEngine] == 'number'}
                                                inputLabel={t(x, {
                                                    unitOfMeasure:
                                                        x in input.vesselCharacteristics
                                                            ? scenarioPhysicalData[x as TInputScenario]?.unitOfMeasure[project.data!.project!.unitOfMeasure!]
                                                            : enginePhysicalData[x as TInputEngine]?.unitOfMeasure[project.data!.project!.unitOfMeasure!],
                                                })}
                                                name={x}
                                                customOnChange={x in input.vesselCharacteristics ? handleChangeVessel : handleChangeEngine}
                                            />
                                        );
                                    }
                                })}
                            </AccordionDetails>
                        </Accordion>
                    </form>
                </Stack>
            )}
        </>
    );
};

export default Input;
